import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Modal } from '@/components/modals/new/modal';
import { useModal } from '@/components/modals/new/use-modal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { getProjects } from '@/features/projects/api/projects';
import { useDebouncedValue } from '@/hooks/use-debounced-value';

import { flatfinderRequest, initializeFlatfinder } from './api';

type FlatfinderListItem = {
  id: string;
  publicId: string;
  projectId: string;
  projectName: string;
  status: string;
};

export function FlatfinderToolsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const createModal = useModal();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);
  const [projectId, setProjectId] = useState('');
  const [busy, setBusy] = useState(false);
  const finders = useQuery({
    queryKey: ['flatfinders'],
    queryFn: () => flatfinderRequest<FlatfinderListItem[]>('/flatfinders'),
  });
  const projects = useQuery({
    queryKey: ['flatfinder-project-picker', debouncedSearch],
    enabled: createModal.isOpen,
    queryFn: () =>
      getProjects({
        query: {
          page: 1,
          pageSize: 30,
          ...(debouncedSearch.trim() ? { filters: { searchString: debouncedSearch.trim() } } : {}),
        },
      }),
  });
  useEffect(() => {
    if (finders.error) toast.error(t('flatfinderFormLoadFindersFailed'));
  }, [finders.error, t]);
  useEffect(() => {
    if (projects.error) toast.error(t('flatfinderFormLoadProjectsFailed'));
  }, [projects.error, t]);
  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t('flatfinderFormFlatfinder')}</h1>
          <p className="text-slate-600">{t('flatfinderFormToolsDescription')}</p>
        </div>
        <Button type="button" onClick={createModal.open}>
          {t('flatfinderFormCreateFlatfinder')}
        </Button>
      </header>
      <Modal
        {...createModal}
        title={t('flatfinderFormLinkProject')}
        dismissDisabled={busy}
        className="rounded-lg border border-primary-light/20 shadow-lg"
      >
        <Modal.Body>
          <form
            className="flex flex-col gap-4"
            onSubmit={async (event) => {
              event.preventDefault();
              if (!projectId) return;
              setBusy(true);
              try {
                if (!finders.data?.some((finder) => finder.projectId === projectId))
                  await initializeFlatfinder(projectId);
                await navigate({ to: '/projects/$projectId/flatfinder', params: { projectId } });
              } catch {
                toast.error(t('flatfinderFormCreateFailed'));
              } finally {
                setBusy(false);
              }
            }}
          >
            <p className="text-sm text-slate-600">{t('flatfinderFormOnePerProject')}</p>
            <Input
              label={t('flatfinderFormFindProject')}
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setProjectId('');
              }}
              placeholder={t('flatfinderFormSearchProject')}
            />
            <Select
              label={t('projectRequired')}
              disabled={busy || projects.isPending}
              value={projectId}
              onChange={({ target }) => setProjectId(target.value)}
              placeholder={t('flatfinderFormSelectProject')}
              options={(projects.data ?? []).map((project) => ({
                id: project.id,
                title: `${project.name || t('flatfinderFormUnnamedProject')}${
                  finders.data?.some((finder) => finder.projectId === project.id)
                    ? t('flatfinderFormHasFlatfinder')
                    : ''
                }`,
              }))}
              disableClear
            />
            {projects.data?.length === 0 && (
              <p className="text-sm text-slate-600">{t('flatfinderFormNoProjects')}</p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" disabled={busy} onClick={createModal.dismiss} variant="outline">
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={busy || !projectId} loading={busy}>
                {busy ? t('opening') : t('flatfinderFormCreateOrOpen')}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      {finders.isPending && <p>{t('flatfinderFormLoadingFinders')}</p>}
      {finders.data?.length === 0 && (
        <div className="rounded-xl border border-dashed p-8 text-center text-slate-600">
          {t('flatfinderFormNoFinders')}
        </div>
      )}
      <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {finders.data?.map((finder) => (
          <li key={finder.id}>
            <Card className="transition-opacity hover:opacity-80">
              <Link
                to="/projects/$projectId/flatfinder"
                params={{ projectId: finder.projectId }}
                className="flex min-h-32 flex-col justify-between gap-3 p-5"
              >
                <h2 className="text-lg font-semibold">{finder.projectName}</h2>
                <span className="text-sm text-slate-600">
                  {finder.status === 'published' ? t('published') : t('draft')} ·{' '}
                  {t('flatfinderFormOpenFlatfinder')}
                </span>
              </Link>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}
